import type { TicketFeeMode } from '@pulsify/shared'
import type { TicketSettings } from '@pulsify/shared/dto'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { feeRuleLabel, planName } from '~/entities/subscription/plans'
import { useSetTicketFeeMode } from '~/entities/ticket/model/use-tickets'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { PayoutSetupButton } from './payout-setup'

function Row({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5 py-3 first:pt-0 last:pb-0">
      <p className="font-medium text-sm">{title}</p>
      {children}
    </div>
  )
}

export function TicketSettingsPanel({
  businessId,
  settings,
}: {
  businessId: string
  settings: TicketSettings
}) {
  const setFeeMode = useSetTicketFeeMode(businessId)
  const { connect } = settings
  const feeMode = (setFeeMode.isPending && setFeeMode.variables) || settings.feeMode

  const choose = (next: TicketFeeMode) => {
    if (next === feeMode) return
    setFeeMode.mutate(next, {
      onSuccess: () => toast.success(m.ticketsFeeSaved()),
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })
  }

  return (
    <div className="divide-y divide-border">
      <Row title={m.ticketsFeeTitle()}>
        <p className="text-muted-foreground text-sm">
          {m.ticketsFeeLine({ fee: feeRuleLabel(settings.fee), plan: planName(settings.tier) })}
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
          <span className="text-sm">{m.ticketsFeePaidBy()}</span>
          <SegmentedControl
            size="sm"
            label={m.ticketsFeePaidBy()}
            value={feeMode}
            options={[
              { value: 'buyer', label: m.ticketsFeeBuyer() },
              { value: 'organizer', label: m.ticketsFeeOrganizer() },
            ]}
            onChange={choose}
          />
        </div>
        <p className="text-muted-foreground text-sm">
          {feeMode === 'buyer' ? m.ticketsFeeBuyerHint() : m.ticketsFeeOrganizerHint()}
        </p>
      </Row>
      <Row title={m.ticketsPayoutsTitle()}>
        {connect.onboardingComplete ? (
          <p className="text-muted-foreground text-sm">
            {connect.payoutsEnabled ? m.ticketsPayoutsReady() : m.ticketsPayoutsChecking()}
          </p>
        ) : connect.available ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 flex-1 text-muted-foreground text-sm">
              {connect.accountId ? m.ticketsPayoutsUnfinished() : m.ticketsPayoutsNotSet()}
            </p>
            <PayoutSetupButton
              businessId={businessId}
              started={connect.accountId !== null}
              variant="outline"
            />
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{m.ticketsPaidSoon()}</p>
        )}
      </Row>
    </div>
  )
}
